import { EventHandler } from './event-handler'

/**
 * Class representing a queue for Event Tracker.
 * This class extends EventHandler and manages a queue of events to be tracked.
 * @extends EventHandler
 */
export class EventTrackerQueue extends EventHandler {
  /**
   * The event tracker instance.
   * @private
   * @type {import('./event-tracker').EventTracker}
   */
  _tracker

  /**
   * The track command factory instance.
   * @private
   * @type {import('./track-event-command-factory').TrackEventCommandFactory}
   */
  _trackCommands

  /**
   * The queue of events to be processed.
   * @private
   * @type {import('./event').Event[]}
   */
  _queue = []

  /**
   * Creates an instance of EventTrackerQueue.
   * @param {import('./event-tracker').EventTracker} tracker - The event tracker instance.
   * @param {import('./track-event-command-factory').TrackEventCommandFactory} trackCommands - The track command factory instance.
   */
  constructor(tracker, trackCommands) {
    super()
    this._tracker = tracker
    this._trackCommands = trackCommands
  }

  /**
   * Returns the current size of the queue.
   * @returns {number} The number of events in the queue.
   */
  size() {
    return this._queue.length
  }

  /**
   * Checks if an event is already queued.
   * @param {import('./event').Event} event - The event to check.
   * @returns {boolean} True if the event is already queued, false otherwise.
   */
  isQueued(event) {
    return this._queue.some((queuedEvent) => queuedEvent.isEquals(event))
  }

  /**
   * Adds an event to the queue if it's not already present.
   * @param {import('./event').Event} event - The event to queue.
   */
  queue(event) {
    if (!this.isQueued(event)) {
      this._queue.push(event)
    }
  }

  /**
   * Processes all events in the queue if the tracker is available.
   * Each event is removed from the queue after processing.
   */
  process() {
    if (this._tracker.isAvailable()) {
      while (this._queue.length > 0) {
        const event = this._queue.shift()
        const command = this._trackCommands.retrieve(event.getType())
        command(this._tracker, event.getPayload())
      }
    }
  }

  /**
   * Handles an event by queueing its payload.
   * @param {import('./event').Event} event - The event to be handled.
   * @override
   */
  handle(event) {
    console.log(event)
    this.queue(event)
  }
}
